import { useCallback, useMemo } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import * as state from '@/components/Analysis/state';
import {
  FileAttributes,
  useUpdatePointOfInterestFileMutation,
  FileUploadStatus,
  useUploadAttachmentsToPoiMutation,
  AllPointsOfInterestForInspectionDataLoaderDocument,
  SelectedPointOfInterestDocument,
  ImageType,
} from '@/__generated__/graphql';
import { Alert } from '@mui/material';
import {
  getSizeFromBase64,
  uploadBase64ToUrl,
} from '../PointOfInterestTab/SelectedPoiContent/Tabs/UploadFileToUrl';
import { ScreenshotLocationType } from '@/types';
import { canvasToImage } from '@/utils/dom';
import {
  BLISTER,
  BLISTER_TYPE,
  DEFAULT_SCREENSHOT_DARK_BACKGROUND,
  DEFAULT_SCREENSHOT_LIGHT_BACKGROUND,
  PIT_TYPE,
} from '@/constants';
import { useCaptureScreenshot } from '@/hooks/useCaptureScreenshot';
import { usePoiLabelMarkerColors } from '@/hooks/usePoiLabelMarkerColors';
import {
  blisterPolygons,
  editPointOfInterest as editCurrentPoi,
  selectedPointOfInterest,
  selectedTemplate,
} from '@/components/Analysis/modules/pointOfInterest/state';
import { convertTemplateNameToPoiType, upperSnakeCase } from '@/utils';

export type SceneOptions = {
  showPointCloudInSpherical?: boolean;
  arePoisVisible?: boolean;
  areSphericalsVisible?: boolean;
  showPointOfInterestPointer?: boolean;
};

type SaveScreenshotProps = {
  location?: ScreenshotLocationType;
  poiId: string | undefined;
  setIsUpdating?: React.Dispatch<React.SetStateAction<boolean>>;
  isUpdating?: boolean;
  refetchPoi?: boolean;
  showSnackbar?: boolean;
  autoCapture?: boolean;
};

type ImageTypeProps = {
  imageType: ImageType;
};

type ViewPointProps = {
  background?:
    | typeof DEFAULT_SCREENSHOT_DARK_BACKGROUND
    | typeof DEFAULT_SCREENSHOT_LIGHT_BACKGROUND;
};

export const useSavePoiScreenshot = ({ imageType }: ImageTypeProps) => {
  const selectedSpherical = useAtomValue(state.selectedSpherical);
  const callbacks = useAtomValue(state.globalCallbacks);
  const setSnackbarMessage = useSetAtom(state.snackbarMessage);
  const [uploadAttachmentsToPoi] = useUploadAttachmentsToPoiMutation();
  const [updatePoiFile] = useUpdatePointOfInterestFileMutation();
  const setAreSphericalsVisible = useSetAtom(state.areSphericalsVisible);
  const setShowPointCloudInSpherical = useSetAtom(state.showPointCloudInSpherical);
  const setArePoisVisible = useSetAtom(state.arePoisVisible);
  const setPolygons = useSetAtom(blisterPolygons);
  const currentPoi = useAtomValue(selectedPointOfInterest);
  const editPointOfInterest = useAtomValue(editCurrentPoi);
  const selectedPoiTemplate = useAtomValue(selectedTemplate);
  const isMaskPoi = selectedPoiTemplate
    ? selectedPoiTemplate.name === BLISTER
    : currentPoi?.blister ?? false;
  const name = editPointOfInterest?.formState
    ? editPointOfInterest?.template?.key ?? editPointOfInterest?.template?.name
    : currentPoi?.name ?? undefined;
  const poiType = convertTemplateNameToPoiType(upperSnakeCase(name));
  const currentSpherical = useAtomValue(state.viewerCurrentSpherical);
  const [showPointOfInterestPointer, setShowPointOfInterestPointer] = useAtom(
    state.showPointOfInterestPointer
  );
  const poiName = useMemo(() => {
    return poiType === BLISTER_TYPE
      ? BLISTER_TYPE
      : poiType === PIT_TYPE
      ? PIT_TYPE
      : currentPoi?.type;
  }, [currentPoi, poiType]);
  const { captureElement } = useCaptureScreenshot();
  const { setPdfPoiLabelMarkerColors, resetPoiLabelMarkerColors } = usePoiLabelMarkerColors();
  const getViewpointScreenshot = useCallback(
    ({ background = DEFAULT_SCREENSHOT_DARK_BACKGROUND }: ViewPointProps) => {
      const container = document.getElementById('outer-viewer');

      if (!container) {
        const abyssViewerParent = document.querySelector('canvas');

        if (!abyssViewerParent) {
          const htmlCanvas = callbacks?.getHTMLCanvas && callbacks.getHTMLCanvas();
          return Promise.resolve(canvasToImage(htmlCanvas, { fillStyle: background }));
        }

        return captureElement({ element: abyssViewerParent, background });
      }

      return captureElement({ element: container, background });
    },
    [callbacks, captureElement]
  );

  // Usage example:
  //    prevOptions = setSceneOptions(myOptions);
  //    setTimeout(()=>{
  //          handleSavePoiScreenshot();
  //          setSceneOptions(prevOptions);   }, 500);
  const setSceneOptions = useCallback(
    (options: SceneOptions): SceneOptions => {
      const prevOptions: SceneOptions = {};

      const { showPointCloudInSpherical, arePoisVisible, areSphericalsVisible } = options;

      if (showPointCloudInSpherical !== undefined) {
        setShowPointCloudInSpherical((current) => {
          prevOptions['showPointCloudInSpherical'] = current;
          return showPointCloudInSpherical;
        });
      }
      if (arePoisVisible !== undefined) {
        setArePoisVisible((current) => {
          prevOptions['arePoisVisible'] = current;
          return arePoisVisible;
        });
      }
      if (areSphericalsVisible !== undefined) {
        setAreSphericalsVisible((current) => {
          prevOptions['areSphericalsVisible'] = current;
          return areSphericalsVisible;
        });
      }
      if (showPointOfInterestPointer !== undefined) {
        setShowPointOfInterestPointer((current) => {
          prevOptions['showPointOfInterestPointer'] = current;
          return showPointOfInterestPointer;
        });
      }
      return prevOptions;
    },
    [
      showPointOfInterestPointer,
      setShowPointCloudInSpherical,
      setArePoisVisible,
      setAreSphericalsVisible,
      setShowPointOfInterestPointer,
    ]
  );

  const handleSavePoiScreenshot = useCallback(
    async ({
      location = 'screenshots',
      poiId,
      isUpdating,
      setIsUpdating,
      autoCapture,
    }: SaveScreenshotProps) => {
      const pointOfInterestScreenShot: FileAttributes[] = [];
      const base64Images: string[] = [];
      let screenshotFileForPdf: FileAttributes;
      if (isUpdating) {
        return setSnackbarMessage({
          shouldShow: true,
          content: (
            <Alert severity="warning">
              Unable to take Screenshot at the moment. Other files are uploading. Please try once
              uploading is done
            </Alert>
          ),
        });
      } else {
        if (setIsUpdating) setIsUpdating(true);

        // Robust approach: Wait for both React state updates and 3D rendering
        const waitForRenderComplete = () => {
          return new Promise<void>((resolve) => {
            // First wait for React state updates
            requestAnimationFrame(() => {
              // Then wait for potential 3D render cycles
              setTimeout(() => {
                // Final RAF to ensure everything is painted
                requestAnimationFrame(() => {
                  resolve();
                });
              }, 100); // Small delay for 3D rendering
            });
          });
        };

        // Ensure pointer is visible before taking screenshots
        const prevPointerState = showPointOfInterestPointer;
        if (autoCapture) {
          setShowPointOfInterestPointer(true);
          await waitForRenderComplete();
        }
        const viewpointScreenshot = await getViewpointScreenshot({
          background: DEFAULT_SCREENSHOT_DARK_BACKGROUND,
        });
        base64Images.push(viewpointScreenshot ?? '');

        // Set PDF annotation colors before taking PDF screenshot
        setPdfPoiLabelMarkerColors();

        // Ensure pointer is still visible for PDF screenshot and wait for render complete
        if (autoCapture) {
          setShowPointOfInterestPointer(true);
          await waitForRenderComplete();
        }

        let screenshotForPdf;
        if (!currentSpherical) {
          try {
            screenshotForPdf = await getViewpointScreenshot({
              background: DEFAULT_SCREENSHOT_LIGHT_BACKGROUND,
            });
          } finally {
            // Reset annotation colors after PDF screenshot (even if it fails)
            resetPoiLabelMarkerColors();
          }
          base64Images.push(screenshotForPdf ?? '');
        }

        // Only reset pointer state if we changed it (after both screenshots are taken)
        if (!prevPointerState) {
          setShowPointOfInterestPointer(false);
        }
        if (!viewpointScreenshot || !poiId) {
          if (setIsUpdating) setIsUpdating(false);
          return '';
        }

        // Always save to files instead of screenshots
        // using same uuid for both files so that while filtering files we can check if white background image exists for that particular image or not
        const uuid = `file-${Date.now()}`;
        const screenshotFile: FileAttributes = {
          uuid,
          name: `POI_SCREENSHOT_${Date.now()}`,
          mimeType: 'image/jpeg',
          size: getSizeFromBase64(viewpointScreenshot),
          imageType: imageType,
        };
        pointOfInterestScreenShot.push(screenshotFile);
        if (screenshotForPdf) {
          screenshotFileForPdf = {
            uuid: `${uuid}-${ImageType.WhiteBgPdf}`,
            name: `POI_SCREENSHOT_PDF_${Date.now()}`,
            mimeType: 'image/jpeg',
            size: getSizeFromBase64(screenshotForPdf),
            imageType: ImageType.WhiteBgPdf,
          };
          pointOfInterestScreenShot.push(screenshotFileForPdf);
        }

        let poiMaskScreenShot: string | undefined;
        if (autoCapture && isMaskPoi) {
          // Ensure pointer is visible and markers are hidden before setting up mask
          setShowPointOfInterestPointer(true);
          await waitForRenderComplete();

          const sceneOptions: SceneOptions = {
            areSphericalsVisible: false,
            ...(currentSpherical
              ? {
                  showPointCloudInSpherical: false,
                  arePoisVisible: false,
                }
              : {}),
          };
          const prevSceneOptions = setSceneOptions(sceneOptions);

          setPolygons((prev) => {
            if (!prev) return prev;
            const polygon = prev.find((polygon) => polygon.id?.startsWith(poiId));
            if (!polygon) return prev;
            polygon.isVisible = true;
            return [...prev];
          });

          // Wait for all state updates and 3D rendering to complete
          await waitForRenderComplete();
          await waitForRenderComplete(); // Double wait for complex state changes

          poiMaskScreenShot = await getViewpointScreenshot({
            background: DEFAULT_SCREENSHOT_DARK_BACKGROUND,
          });
          base64Images.push(poiMaskScreenShot ?? '');

          // Clean up: hide polygon and restore scene
          setPolygons((prev) => {
            if (!prev) return prev;
            const polygon = prev.find((polygon) => polygon.id?.startsWith(poiId));
            if (!polygon) return prev;
            polygon.isVisible = false;
            return [...prev];
          });
          setSceneOptions(prevSceneOptions);
          await waitForRenderComplete();
          if (!poiMaskScreenShot) return;
          const poiProfileMaskFile = {
            uuid,
            name: `${poiName}_MASK_VIEW_${Date.now()}`,
            mimeType: 'image/jpeg',
            size: getSizeFromBase64(poiMaskScreenShot),
            imageType: ImageType.Other,
          };
          pointOfInterestScreenShot.push(poiProfileMaskFile);
        }

        const mutationVariables = {
          input: {
            pointOfInterestId: poiId,
            files: [...pointOfInterestScreenShot],
            lastModifiedFor: 'Capture View',
          },
        };

        const { data } = await uploadAttachmentsToPoi({
          variables: mutationVariables,
          refetchQueries: [AllPointsOfInterestForInspectionDataLoaderDocument],
          onError: (error) => {
            setSnackbarMessage({
              shouldShow: true,
              content: (
                <Alert severity="error" variant="filled">
                  {error.message}
                </Alert>
              ),
            });
          },
        });

        if (data?.uploadAttachmentsToPoi && data?.uploadAttachmentsToPoi?.length > 0) {
          for (let index = 0; index < data.uploadAttachmentsToPoi.length; index++) {
            const uploadedFiles = data.uploadAttachmentsToPoi[index];
            const response = await uploadBase64ToUrl(base64Images[index], uploadedFiles.url);
            if (response) {
              updatePoiFile({
                variables: {
                  input: {
                    pointOfInterestId: poiId,
                    fileId: uploadedFiles.id,
                    uploadStatus: FileUploadStatus.Success,
                  },
                },
                refetchQueries: [SelectedPointOfInterestDocument],
                onCompleted: () => {
                  // Only show success message for manual screenshots, not auto snapshots
                  if (location !== 'attachments') {
                    setSnackbarMessage({
                      shouldShow: true,
                      content: (
                        <Alert severity="success" variant="filled">
                          Screenshot has been successfully uploaded
                        </Alert>
                      ),
                    });
                  }
                },
                onError: (error) => {
                  if (setIsUpdating) setIsUpdating(false);
                  setSnackbarMessage({
                    shouldShow: true,
                    content: (
                      <Alert severity="error" variant="filled">
                        {error.message}
                      </Alert>
                    ),
                  });
                },
              });
            }
          }
          if (setIsUpdating) setIsUpdating(false);
        }
      }
    },
    [
      setSnackbarMessage,
      getViewpointScreenshot,
      setPdfPoiLabelMarkerColors,
      setShowPointOfInterestPointer,
      showPointOfInterestPointer,
      imageType,
      uploadAttachmentsToPoi,
      resetPoiLabelMarkerColors,
      updatePoiFile,
      selectedSpherical,
      currentSpherical,
      setSceneOptions,
      setPolygons,
      poiName,
      isMaskPoi,
    ]
  );

  return { handleSavePoiScreenshot, setSceneOptions };
};
